<template lang="pug">
  .content(:class="[spacingTop, spacingBottom]", v-if="contents && contents.length" )
    .content-wrap.clearfix(ref="containerFull")
      Heading(:heading="heading.title", :subHeading="heading.sub_title", :showLine="showLine", :textAlignment="textAlignment")
      .img-text-wrap(:class="[{ 'float-text' : textFloat }, imagePosition, aspectRatio[1]]")
        .image(v-if="images && images.length", ref="containerSmall")
          PostImage(
            v-for="image in images", 
            :key="image.id",
            :content="image",
            :count="images.length",
            :max-width="containerWidth",
            :aspectRatio="aspectRatio[0]",
            :shadow="imageShadow"
          )
        .content.rte-area(v-if="content && content.text" v-html="content.text")
</template>

<script>
import { debounce } from '@/helpers';

import Heading from './Heading';
import PostImage from './PostImage';

export default {
    name: 'Post',
    data() {
        return {
            containerWidth: undefined
        };
    },
    props: {
        section: {
            type: Object,
            required: true
        }
    },
    components: {
        Heading,
        PostImage
    },
    computed: {
        contents() {
            return this.section.contents || [];
        },
        heading() {
            return this.contents.find(content => content.key === 'heading');
        },
        content() {
            return this.contents.find(content => content.key === 'content');
        },
        images() {
            return this.contents.filter(content => content.key === 'image');
        },
        textAlignment() {
            let alignment = 'text-center';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'text_alignment');
                if (setting && setting.value !== undefined) alignment = setting.value;
            }

            return alignment;
        },
        showLine() {
            let showLine = false;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'show_line');
                if (setting && setting.value !== undefined) showLine = setting.value;
            }

            return showLine;
        },
        imageShadow() {
            let shadow = true;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'image_shadow');
                if (setting && setting.value !== undefined) shadow = setting.value;
            }
            return shadow;
        },
        spacingTop() {
            let spacingTop = 'spacingTopStandard';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'spacingTop');
                if (setting && setting.value !== undefined) spacingTop = setting.value;
            }
            return spacingTop;
        },

        spacingBottom() {
            let spacingBottom = 'spacingBottomStandard';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'spacingBottom');
                if (setting && setting.value !== undefined) spacingBottom = setting.value;
            }
            return spacingBottom;
        },

        aspectRatio() {
            let aspectRatio = '16:9';
            let aspectRatioClass = 'aspectRatio_169';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'aspectRatio');
                if (setting && setting.value !== undefined) aspectRatio = setting.value;

                const split = aspectRatio.split(':');
                aspectRatioClass = 'aspectRatio_' + split[0] + split[1];
            }
            return [aspectRatio, aspectRatioClass];
        },

        imagePosition() {
            let position = 'left-float';

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'image_position');
                if (setting && setting.value !== undefined) position = setting.value;
            }

            return position;
        },
        textFloat() {
            let textFloat = true;

            if (this.section.settings && this.section.settings.length) {
                const setting = this.section.settings.find(setting => setting.key === 'text_float');
                if (setting && setting.value !== undefined) textFloat = setting.value;
            }

            return textFloat;
        }
    },
    mounted() {
            const calculateMaxWidth = () => {
                if(this.imagePosition === 'top-block') {
                    if (this.$refs.containerFull) {
                        const style = getComputedStyle(this.$refs.containerFull);
                        this.containerWidth = this.$refs.containerFull.clientWidth;
                        this.containerWidth -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
                    }
                } else {
                    if (this.$refs.containerSmall) {
                        const style = getComputedStyle(this.$refs.containerSmall);
                        this.containerWidth = this.$refs.containerSmall.clientWidth;
                        this.containerWidth -= parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
                    }
                }
            };

            window.addEventListener('resize', debounce(calculateMaxWidth, 100));
            calculateMaxWidth();
    }
};
</script>

<style lang="sass" scoped>
@import "~@/assets/sass/variables"
.content
  @apply w-full
  line-height: 24px
  .content-wrap
    .content.rte-area
        margin-top: -6px
        @screen max-md
            @apply mt-6
    .img-text-wrap
        @apply flex flex-row
        @screen max-md
            @apply flex-col
            .image
                @apply mt-4

        // Picture Full Size between Title an Desc
        &.top-block
            @apply flex-col
            .content
                @apply mt-6

        // AspectRatio 1:1 // Default
        &.right-float,
        &.left-float
          .image
            @screen md
              @apply flex-shrink-0
              flex-basis: 265px
              width: 265px
        
        // AspectRatio 2:1
        &.right-float.aspectRatio_169,
        &.left-float.aspectRatio_169
          .image    
              @apply flex-shrink-0
              flex-basis: 300px
              width: 300px
        &.right-float
          .image
            @screen md
                @apply order-2
                margin-left: 25px
          .content
            @screen md
                @apply order-1
        &.left-float .image
            @screen md
                margin-right: 25px
    
    // Text does not wrap around the image
    .img-text-wrap.float-text
      @apply block
      &.left-float .image
        @screen md
            @apply float-left
            margin-right: 25px
            margin-bottom: 10px
            padding-top: 6px
      &.right-float .image
        @screen md
            @apply float-right
            padding-top: 6px
            margin-bottom: 10px
            margin-left: 25px
</style>
